/**
 * Webhooks - tenant settings sub-page.
 *
 * Mounted at /settings/webhooks. Lists every webhook subscription this
 * tenant has created, with at-a-glance stats. Row click drills into
 * the detail page; the New Webhook button drops into the create flow.
 *
 * Future Pulse products (email triggers, workflow actions) get their
 * own pages at /settings/email-triggers and /settings/workflow-actions
 * when they ship UIs - this page stays webhooks-only.
 *
 * Header pattern matches ``api-keys-tab.tsx`` exactly (15px h2 +
 * 12px subtitle + right-aligned actions). Stat cards mirror the
 * ``<Metric>`` shape used on the main dashboard.
 */

import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { BookOpen, Plus, Radio } from 'lucide-react'

import { DataTable, type DataTableColumn } from '@/admin/components/data-table'
import { useFormatting } from '@/admin/hooks/use-formatting'
import { usePulseStats } from '@/admin/hooks/use-pulse-stats'
import { usePulseWebhooks } from '@/admin/hooks/use-pulse-webhooks'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { cn } from '@/shared/lib/utils'
import type {
  PulseDerivedStatus,
  PulseWebhookSubscription,
} from '@/admin/types/pulse'

// --- Local helpers --------------------------------------------------------

type FilterKey = 'all' | 'inactive'

function StatusBadge({ status }: { status: PulseDerivedStatus }) {
  if (status === 'healthy')
    return (
      <Badge
        variant="outline"
        className="border-[var(--status-ok-border)] bg-[var(--status-ok-bg)] text-[var(--status-ok)]"
      >
        <span className="mr-1 inline-block h-1.5 w-1.5 rounded-full bg-[var(--status-ok)]" />
        Active
      </Badge>
    )
  if (status === 'degraded')
    return (
      <Badge
        variant="outline"
        className="border-[var(--gold-border)] bg-[var(--gold-bg)] text-[var(--gold-ink)]"
      >
        <span className="mr-1 inline-block h-1.5 w-1.5 rounded-full bg-[var(--gold-ink)]" />
        Degraded
      </Badge>
    )
  return (
    <Badge variant="outline" className="text-[var(--ink-3)]">
      <span className="mr-1 inline-block h-1.5 w-1.5 rounded-full bg-[var(--ink-3)]" />
      Inactive
    </Badge>
  )
}

function Metric({
  label,
  value,
  sub,
}: {
  label: string
  value: string
  sub?: string
}) {
  return (
    <div className="rounded-[8px] bg-[var(--bg-subtle)] px-[18px] py-4">
      <p className="mb-2 text-[10px] font-normal uppercase tracking-[0.14em] text-[var(--ink-3)]">
        {label}
      </p>
      <p className="font-display text-[28px] leading-none text-[var(--ink)]">
        {value}
      </p>
      {sub && <p className="mt-1.5 text-[11px] text-[var(--ink-3)]">{sub}</p>}
    </div>
  )
}

// --- Page -----------------------------------------------------------------

export function PulseSubscriptionsTab() {
  const navigate = useNavigate()
  const { formatNumber, formatDate } = useFormatting()
  const stats = usePulseStats()
  const subs = usePulseWebhooks()
  const [filter, setFilter] = useState<FilterKey>('all')

  const allRows = useMemo(
    () => subs.data?.results ?? [],
    [subs.data?.results],
  )

  const counts = useMemo(() => {
    let inactive = 0
    for (const s of allRows) if (!s.is_active) inactive += 1
    return { all: allRows.length, inactive }
  }, [allRows])

  const rows = useMemo(() => {
    if (filter === 'inactive') return allRows.filter((s) => !s.is_active)
    return allRows
  }, [allRows, filter])

  const columns: DataTableColumn<PulseWebhookSubscription>[] = useMemo(
    () => [
      {
        id: 'subscription',
        header: 'Endpoint',
        cell: (row) => (
          <div className="min-w-0">
            <div className="truncate text-[13px] font-medium text-[var(--ink)]">
              {row.description || 'Untitled webhook'}
            </div>
            <div className="mt-0.5 truncate font-mono text-[11px] text-[var(--ink-3)]">
              {row.target_url}
            </div>
          </div>
        ),
      },
      {
        id: 'events',
        header: 'Events',
        cell: (row) => (
          <span className="font-mono text-[12px] text-[var(--ink-3)]">
            {row.enabled_events.length}
          </span>
        ),
      },
      {
        id: 'updated',
        header: 'Updated',
        cell: (row) => (
          <span className="text-[12px] text-[var(--ink-3)]">
            {formatDate(row.updated_at)}
          </span>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        cell: (row) => <StatusBadge status={row.status} />,
      },
    ],
    [formatDate],
  )

  // ── Stats values (graceful fallback when payload is missing) ──
  // Stats endpoint covers every pulse kind; webhook-only filter here
  // keeps the page focused. p95 is webhook-specific already.
  const statValues = stats.data
  const webhookCount = statValues?.by_kind?.WEBHOOK ?? 0
  const activeCountStr = statValues ? formatNumber(webhookCount) : '-'
  const events7dStr = statValues ? formatNumber(statValues.events_7d) : '-'
  const successRateStr =
    statValues?.success_rate_7d != null
      ? `${statValues.success_rate_7d}%`
      : '-'
  const p95Str =
    statValues?.p95_latency_ms != null
      ? `${formatNumber(statValues.p95_latency_ms)} ms`
      : '-'

  return (
    <div className="mt-8">
      {/* Header */}
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h2 className="text-[15px] font-medium text-[var(--ink)]">
            Webhooks
          </h2>
          <p className="mt-0.5 max-w-[600px] text-[12px] text-[var(--ink-3)]">
            Subscribe to platform events and deliver them as outbound HTTP
            requests to your endpoint. Each webhook has its own event
            filter, signed payloads, and automatic retries on failure.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/settings/webhooks/catalog')}
          >
            <BookOpen className="mr-1.5 h-3 w-3" />
            Event catalog
          </Button>
          <Button size="sm" onClick={() => navigate('/settings/webhooks/new')}>
            <Plus className="mr-1.5 h-3 w-3" />
            New webhook
          </Button>
        </div>
      </div>

      {/* Stats strip */}
      <div className="mb-6 grid grid-cols-2 gap-2.5 lg:grid-cols-4">
        <Metric
          label="Active webhooks"
          value={activeCountStr}
          sub={
            statValues
              ? `${counts.all} total · ${counts.inactive} inactive`
              : undefined
          }
        />
        <Metric
          label="Events (7d)"
          value={events7dStr}
          sub="across all webhooks"
        />
        <Metric
          label="Success rate"
          value={successRateStr}
          sub={
            statValues?.success_rate_7d != null
              ? 'delivered / (delivered + exhausted)'
              : 'no deliveries yet'
          }
        />
        <Metric
          label="Webhook p95"
          value={p95Str}
          sub={
            statValues?.p95_latency_ms != null ? 'last 7 days' : 'no samples'
          }
        />
      </div>

      {/* Filter tabs */}
      <div className="mb-4 flex items-center gap-0 border-b-[0.5px] border-[var(--rule)]">
        {(
          [
            ['all', 'All', counts.all],
            ['inactive', 'Inactive', counts.inactive],
          ] as const
        ).map(([key, label, n]) => (
          <button
            key={key}
            type="button"
            onClick={() => setFilter(key)}
            className={cn(
              '-mb-px cursor-pointer border-b-[1.5px] border-transparent px-3.5 py-2 text-[12px] font-normal transition-colors',
              filter === key
                ? 'border-b-[var(--ink)] text-[var(--ink)]'
                : 'text-[var(--ink-3)] hover:text-[var(--ink-2)]',
            )}
          >
            {label}
            <span className="ml-1.5 rounded-[8px] bg-[var(--bg-subtle)] px-1.5 py-px font-mono text-[10px] text-[var(--ink-3)]">
              {n}
            </span>
          </button>
        ))}
      </div>

      {/* Empty state or table */}
      {!subs.isLoading && allRows.length === 0 ? (
        <EmptyState onCreate={() => navigate('/settings/webhooks/new')} />
      ) : (
        <DataTable<PulseWebhookSubscription>
          tableId="pulse-webhooks"
          columns={columns}
          data={rows}
          isLoading={subs.isLoading}
          rowKey={(row) => row.id}
          onRowClick={(row) => navigate(`/settings/webhooks/${row.id}`)}
        />
      )}
    </div>
  )
}

// --- Empty state ----------------------------------------------------------

function EmptyState({ onCreate }: { onCreate: () => void }) {
  return (
    <div className="rounded-[8px] border-[0.5px] border-dashed border-[var(--rule-strong)] bg-[var(--bg-subtle)] px-8 py-12 text-center">
      <Radio className="mx-auto mb-3 h-5 w-5 text-[var(--ink-3)]" />
      <h3 className="font-display text-[22px] leading-tight text-[var(--ink)]">
        No webhooks yet
      </h3>
      <p className="mx-auto mt-1.5 max-w-[420px] text-[12px] text-[var(--ink-3)]">
        Create your first webhook to start receiving platform events on
        your endpoint.
      </p>
      <Button size="sm" onClick={onCreate} className="mt-5">
        <Plus className="mr-1.5 h-3 w-3" />
        New webhook
      </Button>
    </div>
  )
}
